import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery } from 'urql';
import DestructiveAction, {
  DestructiveActionDescription,
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import { membershipScope } from '@/components/Membership/Membership.utils';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import { graphql } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';
import { getOrganizationMemberSettingsActions } from '@/screens/OrganizationMemberUpdate/OrganizationMemberUpdate.utils';

const GetOrganizationMemberDelete = graphql(/* GraphQL */ `
  query GetOrganizationMemberDelete(
    $organizationId: uuid!
    $personId: String!
  ) {
    event_person(
      where: {
        event_id: { _eq: $organizationId }
        person_id: { _eq: $personId }
      }
      limit: 1
    ) {
      event_id
      person_id
      person {
        id
        name
        is_managed
      }
      event {
        id
        title
        owner_id
        root_parent_id
        permissions
        event {
          id
          ancestry {
            title
          }
        }
      }
    }
  }
`);

const DeleteOrganizationMember = graphql(/* GraphQL */ `
  mutation DeleteOrganizationMember(
    $organizationId: uuid!
    $personId: String!
  ) {
    delete_event_person_by_pk(event_id: $organizationId, person_id: $personId) {
      event_id
      person_id
    }
  }
`);

export default function OrganizationMemberDelete() {
  const { organizationId, personId } = useLocalSearchParams<{
    organizationId: string;
    personId: string;
  }>();
  const userId = useUserId();
  const { dismissTo } = useRouter();
  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetOrganizationMemberDelete,
    variables: { organizationId, personId },
    requestPolicy: 'network-only',
  });
  const [{ fetching: removing }, removeMember] = useMutation(
    DeleteOrganizationMember,
  );
  const membership = data?.event_person[0];
  const canRemove =
    membership &&
    getOrganizationMemberSettingsActions({
      isManaged: membership.person.is_managed,
      isOwner: membership.event.owner_id === personId,
      isSelf: userId === personId,
      permissions: membership.event.permissions,
    }).removeMembership;

  const onRemove = async () => {
    if (removing || !membership || !canRemove || fetching || error)
      return { success: false };
    const result = await removeMember({ organizationId, personId });
    if (result.error || !result.data?.delete_event_person_by_pk) {
      return { success: false };
    }
    return { success: true };
  };

  const onRemoved = () => {
    if (!membership) return;
    if (userId === personId) {
      dismissTo('/event');
      return;
    }
    dismissTo({
      pathname: '/organization/members',
      params: { organizationId: membership.event.root_parent_id },
    });
  };

  return (
    <>
      <Stack.Screen options={{ title: 'Remove Membership' }} />
      <ContentBoundary
        data={canRemove ? membership : null}
        empty={
          <EmptyPlaceholder message="This membership is unavailable or you do not have permission to remove it." />
        }
        loading={fetching}
        error={error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        errorMessage="Could not load this membership. Please try again."
      >
        {(membership) => (
          <DestructiveAction
            key={`${organizationId}:${personId}`}
            label="Remove Membership"
            submittingLabel="Removing…"
            testID="OrganizationMemberConfirmRemove"
            onAction={onRemove}
            onSuccess={onRemoved}
          >
            <DestructiveActionHeading>
              Remove {membership.person.name} from{' '}
              {membershipScope(membership.event)}?
            </DestructiveActionHeading>
            <DestructiveActionDescription>
              This removes only this membership and its direct role. Access
              through teams, ownership, or other assignments remains.
            </DestructiveActionDescription>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </>
  );
}
